<template>
  <div>
    <page-header
      :actions="headerActions"
      left-side-class="col-xl-4 pb-2"
      :page="headerPage"
      right-side-class="col-xl-8 align-items-end d-flex pb-4 justify-content-end"
    />
    <board v-if="model" class="mb-4" :details="board">
      <template #data-item="{ item }">
        <div v-if="item.status">
          <custom-balloon
            :background-color="item.status.color"
            dot
            :text="item.status.name"
          />
        </div>
      </template>
    </board>

    <div v-if="pendingValidationGroups.length" class="submit-validation mb-4">
      <div class="submit-validation__message">
        <i class="fas fa-bell-exclamation mr-2" />
        {{ submitValidationMessage }}
      </div>
      <div class="submit-validation__groups">
        <div
          v-for="group in pendingValidationGroups"
          :key="group.tabId"
          class="submit-validation__group"
        >
          <div class="submit-validation__title">
            {{ group.title }}
          </div>
          <button
            v-for="item in group.items"
            :key="`${group.tabId}-${item.id}`"
            class="submit-validation__item"
            type="button"
            @click="openPendingItem(group.tabId, item)"
          >
            {{ item.label }}
          </button>
        </div>
      </div>
    </div>

    <q-tab
      :active-tab="activeVisibleTabIndex"
      nav-class="text-nowrap"
      @before-change="beforeTabChange"
      @change="setTab"
    >
      <q-tab-item
        v-for="tab in renderedTabs"
        :key="tab.id"
        :disabled="
          activeTab.id !== tab.id && (loading || activeTab.options.disabled)
        "
        :has-error="!tab.valid"
        lazy
        :tabindex="tab.index"
        :title="tab.title"
      >
        <keep-alive>
          <component
            :id="id"
            :is="tab.id"
            :ref="tab.id"
            v-model="tab.model"
            v-bind="tab.options"
            :status="model.status"
            :title="tab.title"
            :is-read-only="isTabReadOnly(tab)"
            :validation-items="getValidationItems(tab.id)"
            @update:initialModel="setInitialModel(tab, $event)"
            @clear-validation="clearErrorTabs([tab.id])"
          />
        </keep-alive>
      </q-tab-item>
    </q-tab>

    <div v-if="canSave" class="d-flex justify-content-end">
      <action-button
        i="fas fa-save"
        text="Guardar"
        variant="success pl-3 pr-3 my-3"
        @click="save()"
      />
    </div>

    <cancel-referral-modal
      ref="cancelReferralModal"
      @save="onCancelReferralConfirmed"
    />
  </div>
</template>

<script>
//mixins
import storeMixin from '@/mixin/storeGenerator';
import alertMixin from '@/mixin/alert.mixin.js';
import objectToFormData from 'object-to-formdata';

//utils
import { copy, equals } from '@/utils/objUtil';

//filters
import { empty, date } from '@/utils/filters';

//components
import PageHeader from 'PageHeader';
import Board from 'Board';
import CustomBalloon from 'custom-balloon';
import { QTab, QTabItem } from '@/components/common/q-tabs';
import ActionButton from 'ActionButton';
import CancelReferralModal from './components/CancelReferralModal.vue';

//tabs
import { ReferralInfo, Binnacle, Documents, Signature } from './tabs';
import models from './tabs/models';
import { SignatureMechanisms } from './tabs/signature/model';

//services
import {
  cancel,
  changeStatus,
  get,
  getDocuments,
  returnToDraft as returnReferralToDraft,
  save,
  sendReferralToArv,
} from '@/services/vocational-rehabilitation-referral/api.js';
import { getSignatureHubInfo } from '@/services/signatureHub/api';

//constants
import digitalSignatureDocumentStatus from '@/utils/constants/digitalSignatureDocumentStatus';
import SignatureHubTypes from '@/utils/constants/signatureHubTypes';
import Statuses, {
  draft as DraftStatus,
} from '@/utils/constants/vocational-rehabilitation-referral-statuses';
import DocumentRequirementTypes from '@/utils/constants/vocational-rehabilitation-referral-document-requirement-types';

const TabRouteIndexes = Object.freeze({
  ReferralInfo: 0,
  Documents: 1,
  Binnacle: 2,
  Signature: 4,
});

export default {
  name: 'VocationalRehabilitationReferralMain',
  components: {
    PageHeader,
    Board,
    CustomBalloon,
    QTab,
    QTabItem,
    ActionButton,
    CancelReferralModal,
    ReferralInfo,
    Binnacle,
    Documents,
    Signature,
  },
  mixins: [storeMixin, alertMixin],
  props: {
    id: {
      type: [Number, String],
      default: () => null,
    },
  },
  data() {
    return {
      loading: false,
      sendingArv: false,
      cancellingReferral: false,
      pendingValidationGroups: [],
      submitValidationMessage:
        'No es posible completar el referido. Complete la información y los documentos requeridos antes de continuar.',
      tabChangeConfirmed: false,
      model: { status: DraftStatus, id: null },
      canCompleteSignatureDocument: false,
      tabs: [
        models.ReferralInfo,
        models.Documents,
        models.Binnacle,
        models.Signature,
      ]
        .filter(({ options }) => !options.hidden)
        .map((x, index) => ({
          index,
          routeIndex: TabRouteIndexes[x.id],
          ...x,
        })),
    };
  },
  computed: {
    headerPage() {
      return {
        title: 'Referido a Rehabilitación Vocacional (ARV)',
        icon: 'fas fa-user',
        routes: ['Inicio', 'PEI', 'Referido a Rehabilitación Vocacional (ARV)'],
      };
    },
    headerActions() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => this.back(),
        },
        {
          text: this.sendingArv ? 'Enviando...' : 'Enviar a ARV',
          icon: 'fas fa-share',
          variant: this.sendingArv ? 'success disabled' : 'success',
          visible: this.showSendArvButton,
          click: () => this.sendReferral(),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          variant: 'success',
          visible: this.canSave,
          click: () => this.save(),
        },
        {
          text: 'Completar',
          icon: 'fas fa-check-circle',
          variant: 'success',
          visible: this.canComplete,
          click: () => this.save(true),
        },
        {
          text: 'Someter',
          icon: 'fas fa-paper-plane',
          variant: 'success',
          visible: this.canSubmit,
          click: () => this.save(true),
        },
        {
          text: 'Devolver a Borrador',
          icon: 'fas fa-undo',
          variant: 'outline-success',
          visible: this.canReturnToDraft,
          click: () => this.returnToDraft(),
        },
        {
          text: this.cancellingReferral ? 'Cancelando...' : 'Cancelar Referido',
          icon: 'fas fa-ban',
          variant: this.cancellingReferral
            ? 'outline-danger disabled'
            : 'outline-danger',
          visible: this.canCancelReferral,
          click: () => this.openCancelReferralModal(),
        },
      ];
    },
    board() {
      return [
        [
          {
            label: 'Número de Referido:',
            icon: 'fas fa-file-alt',
            cols: 'col-12 col-md',
            data: empty(this.model?.number),
          },
          {
            label: 'Estatus:',
            icon: 'fas fa-file-invoice',
            cols: 'col-12 col-md',
            status: this.model?.status,
          },
          {
            label: 'Nombre del Estudiante:',
            icon: 'fas fa-user',
            cols: 'col-12 col-md',
            data: empty(this.model?.student?.name),
          },
          {
            label: 'Fecha de creación:',
            icon: 'fas fa-calendar-alt',
            cols: 'col-12 col-md',
            data: date(this.model?.date),
          },
        ],
      ];
    },
    isDraft() {
      return this.model.status?.id === Statuses.Draft;
    },
    isReadOnly() {
      return this.model.status?.id !== Statuses.Draft;
    },
    isCompleted() {
      return this.model.status?.id === Statuses.Completed;
    },
    isSubmitted() {
      return this.model.status?.id === Statuses.Submitted;
    },
    isSignatureRejected() {
      return this.model.status?.id === Statuses.SignatureRejected;
    },
    isDocumentCancelled() {
      return this.model.status?.id === Statuses.DocumentCancelled;
    },
    isClosedSignatureStatus() {
      return this.isSignatureRejected || this.isDocumentCancelled;
    },
    isReturnedByArv() {
      return this.model.status?.id === Statuses.ReturnedByArv;
    },
    isSent() {
      return this.model.status?.id === Statuses.Sent;
    },
    isCancelled() {
      return this.model.status?.id === Statuses.Cancelled;
    },
    canCancelReferral() {
      return this.isSubmitted || this.isSent || this.isReturnedByArv;
    },
    canSave() {
      return this.isDraft || this.isCompleted;
    },
    canComplete() {
      return this.isDraft;
    },
    canSubmit() {
      return this.isCompleted || this.isReturnedByArv;
    },
    canReturnToDraft() {
      return (
        this.isClosedSignatureStatus ||
        (this.isCompleted &&
          this.userHasPermissions(
            'pei.vocational-rehabilitation-referral.returntodraft'
          ))
      );
    },
    isSignatureTabVisible() {
      return [
        Statuses.Submitted,
        Statuses.Completed,
        Statuses.SignatureRejected,
        Statuses.DocumentCancelled,
        Statuses.Sent,
      ]
        .concat([])
        .includes(this.model.status?.id);
    },
    renderedTabs() {
      return this.tabs.filter((tab) => this.isTabVisible(tab));
    },
    activeVisibleTabIndex() {
      const index = this.renderedTabs.findIndex(
        (tab) => tab.id === this.activeTab?.id
      );

      return index >= 0 ? index : 0;
    },
    hasSaveOnActive() {
      const { preventBack, preventRouteLeave } = this.activeTab.options;
      return preventBack || preventRouteLeave;
    },
    hasChanges() {
      if (this.isReadOnly) return false;
      const { model, options } = this.activeTab;
      return this.hasSaveOnActive
        ? !equals(model, options.initialModel)
        : false;
    },
    preventTabChangeDisabled() {
      return [
        !this.activeTab.options.preventTabChange,
        !this.hasChanges,
        this.tabChangeConfirmed,
      ].includes(true);
    },
    activeTab: {
      get() {
        const tab = this.$route.query.tab;
        return (
          this.tabs.find(
            (item) => item.routeIndex == tab && this.isTabVisible(item)
          ) || this.tabs.find((item) => this.isTabVisible(item))
        );
      },
      set({ routeIndex: tab }) {
        const current = this.$route.query.tab ?? null;
        const next = tab ? String(tab) : null;
        if (current === next) return;

        if (next) this.$router.replace({ query: { tab: next } });
        else {
          const { name, params } = this.$route;
          this.$router.replace({ name, params });
        }
      },
    },
    showSendArvButton() {
      const statusId = Number(this.model?.status?.id);

      return Statuses.Submitted === statusId;
    },
  },
  async mounted() {
    await this.get(this.id);
  },
  methods: {
    setInitialModel(tab, value) {
      tab.options.initialModel = copy(value);
    },
    setTab(tab) {
      if (this.loading) return;
      this.activeTab = this.renderedTabs[tab] || this.renderedTabs[0];
    },
    isTabVisible(tab) {
      return tab.id !== 'Signature' || this.isSignatureTabVisible;
    },
    isTabReadOnly(tab) {
      if (tab.id === 'Signature') {
        return (
          this.isSubmitted || this.isClosedSignatureStatus || this.isCancelled
        );
      }

      return (
        ['ReferralInfo', 'Documents'].includes(tab.id) &&
        !this.isDraft &&
        !this.isReturnedByArv
      );
    },
    ensureActiveTabVisible() {
      const routeTab = this.$route.query.tab;
      const isRouteTabVisible = this.tabs.some(
        (tab) => tab.routeIndex == routeTab && this.isTabVisible(tab)
      );

      if (!routeTab || isRouteTabVisible) {
        return;
      }

      this.activeTab = this.tabs.find((tab) => this.isTabVisible(tab));
    },
    getValidationItems(tabId) {
      return (
        this.pendingValidationGroups.find((group) => group.tabId === tabId)
          ?.items || []
      );
    },
    async navigateToTab(tab) {
      if (!tab) return;

      const current = this.$route.query.tab ?? null;
      const next = tab.routeIndex ? String(tab.routeIndex) : null;
      if (current !== next) {
        if (next) {
          await this.$router.replace({ query: { tab: next } });
        } else {
          const { name, params } = this.$route;
          await this.$router.replace({ name, params });
        }
      }

      await this.$nextTick();
      await this.$nextTick();
    },
    async beforeTabChange(tab, _, e) {
      if (tab === this.activeVisibleTabIndex) return;
      if (this.preventTabChangeDisabled || this.loading) return;
      e.preventDefault();
      const { cancelled } = await this.confirm();
      if (cancelled) return;
      this.setTab(tab);
    },
    back() {
      // beforeRouteLeave already asks for confirmation on any navigation
      this.$router.push('/pei/vocational-rehabilitation-referral');
    },
    async sendReferral() {
      if (this.sendingArv) return;

      const isConfirmed = await this.ShowCancelModal(
        '¿Confirma enviar este referido a Rehabilitación Vocacional (ARV)?',
        `Una vez enviado, ARV recibirá la información y documentos del referido para su evaluación.`,
        'Sí',
        'No'
      );

      if (!isConfirmed) {
        return;
      }

      this.sendingArv = true;

      try {
        const { data } = await sendReferralToArv(this.id);

        if (data.status == 'ERROR') throw new Error(data.message);

        this.ShowToast(
          'Éxito',
          'El referido fue enviado correctamenta para ARV.',
          'success'
        );

        this.setTab(0);
        await this.get(this.id);
      } catch (error) {
        this.ShowToast(
          'Error',
          error?.response?.data?.message ||
            error?.message ||
            'No fue posible enviar el referido.',
          'error'
        );
        this.setTab(0);
      }
      this.sendingArv = false;
    },
    async openCancelReferralModal() {
      if (this.cancellingReferral) return;
      await this.$refs.cancelReferralModal.open();
    },
    async onCancelReferralConfirmed({ comment }) {
      if (this.cancellingReferral) return;
      this.cancellingReferral = true;

      try {
        await cancel(this.id, {
          comment,
        });

        this.ShowToast(
          'Éxito',
          'El referido fue cancelado y se notificó a Rehabilitación Vocacional (ARV).',
          'success'
        );
      } catch (error) {
        const message =
          error?.response?.data?.message ??
          'No fue posible cancelar el referido. Intente nuevamente.';

        this.ShowToast('Error', message, 'error');
      } finally {
        await this.get(this.id);
        this.cancellingReferral = false;
      }
    },
    async get(id) {
      try {
        this.loading = true;
        const { data } = await get(id);
        if (!data) return;

        this.model = data;
        this.setReferralInfo(data.referralInfo);
        this.setSignature(data.signature);
        await this.$nextTick();
        this.tabs.forEach((tab) => {
          tab.options.initialModel = copy(tab.model);
        });
        if (await this.syncClosedSignatureStatus()) return;
        this.ensureActiveTabVisible();
        this.tabChangeConfirmed = false;
        if (this.id == data.id) return;

        this.$router.push({
          name: this.$route.name,
          params: { ...this.$route.params, id: data.id },
          query: { ...this.$route.query },
        });
      } catch {
        this.ShowToast(
          'Error',
          'No fue posible cargar el referido a rehabilitación vocacional.',
          'error'
        );
      } finally {
        this.loading = false;
      }
    },
    setReferralInfo(referralInfo) {
      const tab = this.tabs.find((x) => x.id === 'ReferralInfo');
      if (!tab) return;
      const model = { referralInfo };
      tab.model = model;
      tab.options.initialModel = copy(model);
    },
    setSignature(signature) {
      const tab = this.tabs.find((x) => x.id === 'Signature');
      if (!tab) return;
      const model = signature || {};
      tab.model = model;
      tab.options.initialModel = copy(model);
    },
    async confirm() {
      if (!this.hasChanges) return { cancelled: false, confirmed: false };
      const { value: confirmed, dismiss } = await this.ShowCancelModalClosable(
        'Confirmación',
        '<div>Cuenta con cambios sin guardar,</div><div>¿Desea guardar la información previamente documentada?</div>'
      );
      const cancelled = ['close', 'backdrop'].includes(dismiss);
      if (cancelled) return { cancelled, confirmed: false };
      if (confirmed) await this.save();
      return { confirmed, cancelled };
    },
    async returnToDraft() {
      const { value: confirmed, dismiss } = await this.ShowCancelModalClosable(
        'Confirmación',
        '¿Está seguro que desea devolver este referido a Borrador?',
        'Sí',
        'No',
        false
      );

      if (['close', 'backdrop'].includes(dismiss) || !confirmed) {
        return;
      }

      try {
        await returnReferralToDraft(this.id);
        this.clearErrorTabs();
        await this.get(this.id);
        this.activeTab = this.tabs.find((tab) => tab.id === 'ReferralInfo');
        this.ShowToast(
          'Éxito',
          'El referido fue devuelto a Borrador exitosamente.',
          'success'
        );
      } catch {
        this.ShowToast(
          'Error',
          'No fue posible devolver el referido a Borrador.',
          'error'
        );
      }
    },
    async syncClosedSignatureStatus() {
      const currentStatusId = Number(this.model?.status?.id);
      if (![Statuses.Completed, Statuses.Submitted].includes(currentStatusId)) {
        return false;
      }

      const signature = this.tabs.find((tab) => tab.id === 'Signature')?.model;
      if (
        Number(signature?.signatureMechanismId) !==
        SignatureMechanisms.Electronic
      ) {
        return false;
      }

      let data = null;
      try {
        ({ data } = await getSignatureHubInfo(
          SignatureHubTypes.REFERRAL_VOCATIONAL_REHABILITATION,
          this.id
        ));
      } catch {
        return false;
      }

      const nextStatusId = this.getReferralStatusFromSignatureDocument(
        data?.documentInfo?.documentStatusId
      );

      if (!nextStatusId || nextStatusId === currentStatusId) {
        return false;
      }

      await changeStatus(this.id, {
        statusId: nextStatusId,
        documentId: data?.documentId,
        comment: this.getClosedSignatureStatusComment(nextStatusId),
      });
      await this.get(this.id);

      return true;
    },
    getReferralStatusFromSignatureDocument(documentStatusId) {
      const statusId = Number(documentStatusId);
      if (statusId === digitalSignatureDocumentStatus.Rejected) {
        return Statuses.SignatureRejected;
      }

      if (
        [
          digitalSignatureDocumentStatus.Cancelled,
          digitalSignatureDocumentStatus.RenderedIneffective,
        ].includes(statusId)
      ) {
        return Statuses.DocumentCancelled;
      }

      return null;
    },
    getClosedSignatureStatusComment(statusId) {
      if (statusId === Statuses.SignatureRejected) {
        return 'Firma rechazada desde el Hub de Firmas Digitales.';
      }

      if (statusId === Statuses.DocumentCancelled) {
        return 'Documento cancelado desde el Hub de Firmas Digitales.';
      }

      return null;
    },
    async save(isSubmitted = false) {
      try {
        const { id, model, build, confirm } = this.activeTab;
        const isSubmittingCompletedReferral = isSubmitted && this.isCompleted;
        if (isSubmitted) {
          this.clearErrorTabs();
          const validationGroups = isSubmittingCompletedReferral
            ? await this.validateSignatureSubmit()
            : await this.validateSubmitTabs();

          if (validationGroups.length) {
            await this.setInvalidTabs(
              validationGroups,
              isSubmittingCompletedReferral
                ? 'Debe contar con el documento firmado (manuscrito o digital) antes de someter el referido.'
                : 'No es posible completar el referido. Complete la información y los documentos requeridos antes de continuar.'
            );
            return;
          }

          const {
            value: confirmed,
            dismiss,
          } = await this.ShowCancelModalClosable(
            'Confirmación',
            isSubmittingCompletedReferral
              ? '¿Está seguro que desea someter el referido a Rehabilitación Vocacional (ARV)?'
              : '¿Está seguro que desea completar el formulario? Una vez completado, la información del referido no podrá editarse.',
            'Sí',
            'No',
            false
          );

          if (['close', 'backdrop'].includes(dismiss) || !confirmed) {
            return;
          }
        }
        const tabModel = copy(model);
        if (!isSubmitted && confirm) {
          const confirmed = await confirm(tabModel, copy(this.model));
          if (!confirmed) return;
        }
        const payload = {
          ...(await build(tabModel)),
          id: this.id && this.id > 0 ? Number(this.id) : null,
          statusId: Number(this.model.status?.id ?? Statuses.Draft),
          isSubmitted,
        };
        const formData = objectToFormData(payload || {}, { indices: true });
        const { data } = await save(formData);
        this.activeTab.options.initialModel = copy(model);
        if (data.invalid) {
          await this.setInvalidTabs(
            await this.mapServerInvalidTabs(data.invalidTabs || data.invalid)
          );
          return;
        }
        await this.get(data.id);
        if (isSubmitted) {
          this.ShowToast(
            'Éxito',
            isSubmittingCompletedReferral
              ? 'El referido fue sometido exitosamente.'
              : 'El formulario fue completado exitosamente.',
            'success'
          );
          return;
        }
        this.ShowToast(
          'Éxito',
          'La información del referido fue guardada correctamente.',
          'success'
        );
      } catch {
        this.ShowToast(
          '',
          'No fue posible guardar la información. Intente nuevamente o contacte al administrador.',
          'error'
        );
      }
    },
    async setInvalidTabs(validationGroups = [], message = null) {
      const groups = validationGroups
        .map((group) => {
          const tab = this.tabs.find((item) => item.id === group.tabId);

          return {
            ...group,
            title: group.title || tab?.title,
            items: group.items?.length
              ? group.items
              : [
                  {
                    id: group.tabId,
                    label: 'Tiene información requerida pendiente.',
                  },
                ],
          };
        })
        .filter((group) => group.title);

      if (groups.length == 0) return;
      this.submitValidationMessage =
        message ||
        'No es posible completar el referido. Complete la información y los documentos requeridos antes de continuar.';
      this.pendingValidationGroups = groups;
      const ids = groups.map((group) => group.tabId);
      const tabs = this.tabs.filter((x) => ids.includes(x.id));
      tabs.forEach((x) => (x.valid = false));
      this.ShowToast('', this.submitValidationMessage, 'error');
    },
    clearErrorTabs(ids = []) {
      const tabs = ids.length
        ? this.tabs.filter((x) => ids.includes(x.id))
        : this.tabs;

      tabs.forEach((x) => (x.valid = true));
      this.pendingValidationGroups = ids.length
        ? this.pendingValidationGroups.filter(
            (group) => !ids.includes(group.tabId)
          )
        : [];
      if (!ids.length) {
        this.submitValidationMessage =
          'No es posible completar el referido. Complete la información y los documentos requeridos antes de continuar.';
      }
    },
    async validateSubmitTabs() {
      const validations = [];

      for (const tabId of ['ReferralInfo', 'Documents']) {
        validations.push(await this.validateTab(tabId));
      }

      return validations.filter((validation) => !validation.valid);
    },
    async validateSignatureSubmit() {
      const validation = await this.validateTab('Signature');

      return validation.valid ? [] : [validation];
    },
    getTabRef(id) {
      const ref = this.$refs[id];
      return Array.isArray(ref) ? ref[0] : ref;
    },
    async validateTab(id) {
      const tabRef = this.getTabRef(id);
      const tab = this.tabs.find((x) => x.id === id);
      if (!tabRef?.validate) {
        return await this.getUnmountedTabValidation(id, tab);
      }
      const result = await tabRef.validate();

      if (typeof result === 'object') {
        return {
          tabId: result.tabId || id,
          title: result.title || tab?.title,
          valid: result.valid,
          items: result.items || [],
        };
      }

      return {
        tabId: id,
        title: tab?.title,
        valid: result,
        items: [],
      };
    },
    async getUnmountedTabValidation(id, tab) {
      const items = await this.getUnmountedTabPendingItems(id, tab);

      return {
        tabId: id,
        title: tab?.title,
        valid: items.length === 0,
        items,
      };
    },
    async getUnmountedTabPendingItems(id, tab) {
      if (id === 'ReferralInfo') {
        return this.getUnmountedReferralInfoPendingItems(
          tab?.model?.referralInfo || {}
        );
      }

      if (id === 'Documents') {
        return await this.getUnmountedDocumentsPendingItems();
      }

      if (id === 'Signature') {
        return await this.getUnmountedSignaturePendingItems(tab?.model || {});
      }

      return [
        {
          id,
          label: 'Tiene información requerida pendiente.',
        },
      ];
    },
    getUnmountedReferralInfoPendingItems(model) {
      const isEmpty = (value) =>
        value === null ||
        value === undefined ||
        value === '' ||
        (typeof value === 'string' && value.trim() === '');
      const hasMinLength = (value, min) =>
        typeof value === 'string' && value.trim().length >= min;
      const add = (id, label, selector) => ({ id, label, selector });
      const medical = model.disabilityMedicalInfo || {};
      const education = model.educationalHistoryInfo || {};
      const additional = model.additionalInformation || {};
      const items = [];

      if (isEmpty(model.referralDate)) {
        items.push(add('referralDate', 'Fecha de Referido.', '.date-field'));
      }

      if (isEmpty(medical.disabilityCategory?.id)) {
        items.push(
          add(
            'disabilityCategory',
            'Categoría de Discapacidad.',
            '.category-field'
          )
        );
      }

      if (isEmpty(medical.isReceivingTreatment)) {
        items.push(
          add(
            'isReceivingTreatment',
            'Indicar si recibe tratamiento.',
            '[name="receiving-treatment"]'
          )
        );
      }

      if (
        medical.isReceivingTreatment === true &&
        isEmpty(medical.treatmentDescription)
      ) {
        items.push(
          add(
            'treatmentDescription',
            'Descripción del tratamiento.',
            '.question-textarea'
          )
        );
      }

      if (isEmpty(medical.usesMedication)) {
        items.push(
          add(
            'usesMedication',
            'Indicar si utiliza medicamento.',
            '[name="uses-medication"]'
          )
        );
      }

      if (
        medical.usesMedication === true &&
        isEmpty(medical.medicationDescription)
      ) {
        items.push(
          add(
            'medicationDescription',
            'Descripción de medicamento y dosis.',
            '.question-textarea'
          )
        );
      }

      if (isEmpty(medical.walksIndependently)) {
        items.push(
          add(
            'walksIndependently',
            'Indicar si camina independientemente.',
            '[name="walks-independently"]'
          )
        );
      }

      if (isEmpty(medical.canTravelAlone)) {
        items.push(
          add(
            'canTravelAlone',
            'Indicar si puede viajar solo.',
            '[name="can-travel-alone"]'
          )
        );
      }

      if (isEmpty(medical.performsDailySelfCareActivities)) {
        items.push(
          add(
            'performsDailySelfCareActivities',
            'Indicar si realiza actividades diarias del cuidado propio.',
            '[name="daily-self-care"]'
          )
        );
      }

      if (isEmpty(education.isCurrentlyStudying)) {
        items.push(
          add(
            'isCurrentlyStudying',
            'Indicar si se encuentra estudiando actualmente.',
            '[name="currently-studying"]'
          )
        );
      }

      if (!education.completedGradeProgram?.id) {
        items.push(
          add(
            'completedGradeProgram',
            'Último grado o programa completado.',
            '.grade-field'
          )
        );
      }

      if (isEmpty(education.hasVocationalInterestEvaluationReport)) {
        items.push(
          add(
            'hasVocationalInterestEvaluationReport',
            'Indicar si cuenta con informe de apreciación o evaluación de intereses vocacionales.',
            '[name="vocational-interest-report"]'
          )
        );
      }

      if (
        education.hasVocationalInterestEvaluationReport === true &&
        !education.vocationalInterestEvaluationDocument &&
        !education.vocationalInterestEvaluationFile
      ) {
        items.push(
          add(
            'vocationalInterestEvaluationFile',
            'Documento del informe de apreciación o evaluación de intereses vocacionales.',
            '.document-row'
          )
        );
      }

      if (isEmpty(education.isWillingToCompleteVocationalGoal)) {
        items.push(
          add(
            'isWillingToCompleteVocationalGoal',
            'Indicar si demuestra disposición para completar un objetivo vocacional.',
            '[name="vocational-goal-willingness"]'
          )
        );
      }

      if (!hasMinLength(additional.referralReason, 20)) {
        items.push(
          add(
            'referralReason',
            'Razón del referido debe tener al menos 20 caracteres.',
            '.additional-information .question-textarea--spaced'
          )
        );
      }

      return items;
    },
    async getUnmountedDocumentsPendingItems() {
      try {
        const { data } = await getDocuments(this.id);
        const nonRequiredTypeIds = [
          DocumentRequirementTypes.StateMeasurementReport,
          DocumentRequirementTypes.GradeReport,
          DocumentRequirementTypes.VocationalInterestInventory,
        ];
        const missingDocuments = (data?.documentTypes || []).filter(
          (type) => !nonRequiredTypeIds.includes(type.id)
        );

        if (!missingDocuments.length) {
          return [];
        }

        return missingDocuments.map((type) => ({
          id: `document-${type.id}`,
          label: type.name,
          selector: '.documents-tab',
        }));
      } catch {
        return [
          {
            id: 'referral-document',
            label: 'No fue posible validar el documento requerido.',
            selector: '.documents-tab',
          },
        ];
      }

      return [
        {
          id: 'referral-document',
          label: 'Tiene documentos requeridos pendientes.',
          selector: '.documents-tab',
        },
      ];
    },
    async getUnmountedSignaturePendingItems(model) {
      const mechanism = Number(model.signatureMechanismId);

      if (!mechanism) {
        return [
          {
            id: 'signatureMechanismId',
            label: 'Seleccione el mecanismo de firma.',
            selector: '.signature-panel',
          },
        ];
      }

      if (mechanism === SignatureMechanisms.Manual) {
        const hasManualSigner = [
          model.evaluatorName,
          model.evaluatorLastName,
          model.evaluatorDate,
        ].every(Boolean);

        if (!hasManualSigner) {
          return [
            {
              id: 'manualSigner',
              label: 'Complete la información del firmante.',
              selector: '.signature-panel',
            },
          ];
        }

        if (!model.signedDocumentId) {
          return [
            {
              id: 'signedDocumentId',
              label: 'Adjunte el documento firmado.',
              selector: '.signature-panel',
            },
          ];
        }

        return [];
      }

      try {
        const { data } = await getSignatureHubInfo(
          SignatureHubTypes.REFERRAL_VOCATIONAL_REHABILITATION,
          this.id
        );
        const isSigned =
          data?.documentInfo?.documentStatusId ===
          digitalSignatureDocumentStatus.Signed;

        if (isSigned) {
          return [];
        }
      } catch {
        return [
          {
            id: 'electronicSignature',
            label: 'No fue posible validar la firma electrónica.',
            selector: '.signature-panel',
          },
        ];
      }

      return [
        {
          id: 'electronicSignature',
          label: 'Complete el proceso de firma electrónica.',
          selector: '.signature-panel',
        },
      ];
    },
    async mapServerInvalidTabs(invalidTabs = []) {
      const groups = [];

      for (const { tab } of invalidTabs || []) {
        const tabId =
          {
            Information: 'ReferralInfo',
            Documents: 'Documents',
            Signature: 'Signature',
          }[tab] || tab;
        if (tabId === 'Binnacle') continue;

        const tabInfo = this.tabs.find((item) => item.id === tabId);
        const validation = await this.validateTab(tabId);

        groups.push({
          tabId,
          title: tabInfo?.title,
          valid: false,
          items: validation.items?.length
            ? validation.items
            : [
                {
                  id: tabId,
                  label: 'Tiene información requerida pendiente.',
                },
              ],
        });
      }

      return groups;
    },
    async openPendingItem(tabId, item) {
      const tab = this.tabs.find((x) => x.id === tabId);
      if (!tab) return;

      this.tabChangeConfirmed = true;
      await this.navigateToTab(tab);
      this.tabChangeConfirmed = false;

      await this.$nextTick();
      const target = item.selector
        ? document.querySelector(item.selector)
        : null;
      if (!target) return;

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const input = target.querySelector?.('input, textarea, button, select');
      input?.focus?.();
    },
  },
  async beforeRouteLeave(_, __, next) {
    try {
      if (this.activeTab.options.preventRouteLeave) {
        const { cancelled } = await this.confirm();
        if (cancelled) return false;
      }
      next();
    } catch {
      return false;
    }
  },
};
</script>

<style scoped>
::v-deep .orange-icon {
  font-size: 18pt;
}

.submit-validation {
  background: #fff7e8;
  border: 1px solid #d99025;
  border-radius: 4px;
  color: #8a5200;
  padding: 14px 16px;
}

.submit-validation__message {
  align-items: center;
  display: flex;
  font-weight: 700;
  margin-bottom: 12px;
}

.submit-validation__groups {
  display: grid;
  gap: 12px;
}

.submit-validation__group {
  background: #fffdf8;
  border: 1px solid #f1d5a6;
  border-radius: 4px;
  padding: 10px 12px;
}

.submit-validation__title {
  color: #17342f;
  font-weight: 700;
  margin-bottom: 6px;
}

.submit-validation__item {
  background: transparent;
  border: 0;
  color: #006c59;
  cursor: pointer;
  display: block;
  padding: 3px 0;
  text-align: left;
  text-decoration: underline;
}

.disabled {
}
</style>
